import { AVAILABILITY_INTERACTION_TIMEOUT_MS } from '@/constants/availability-telemetry.ts';

import { createInteractionTracker } from '@/lib/interaction-telemetry.ts';

import {
    type AvailabilityAction,
    type AvailabilityMode,
    type MetricFailureReason,
    MetricName,
    type MetricOutcome,
    type MetricSource,
} from '@/models/metrics.ts';

/**
 * Availability telemetry: one measurement per operator interaction, from the click to the moment the
 * slots are on screen. The lifecycle lives in `interaction-telemetry.ts`; this file only binds it to
 * the availability vocabulary.
 */

type AvailabilityContext = {
    availability_mode: AvailabilityMode;
    availability_action: AvailabilityAction;
    source?: MetricSource;
};

const interactions = createInteractionTracker<AvailabilityContext>({
    outcomeMetric: MetricName.AVAILABILITY_OUTCOME,
    durationMetric: MetricName.AVAILABILITY_DURATION,
    timeoutMs: AVAILABILITY_INTERACTION_TIMEOUT_MS,
    timeoutReason: 'availability_timeout',
    label: 'availability-telemetry',
});

interface StartParams {
    key: string;
    mode: AvailabilityMode;
    action: AvailabilityAction;
}

/** Begin measuring an interaction. A second start on the same key aborts the one it supersedes. */
const startAvailabilityInteraction = ({ key, mode, action }: StartParams): void =>
    interactions.start({ key: key, context: { availability_mode: mode, availability_action: action } });

interface SettleParams {
    key: string;
    outcome: MetricOutcome;
    /** Failure tag, attached only when `outcome === 'failure'`. */
    reason?: MetricFailureReason;
    source?: MetricSource;
}

/**
 * End an open interaction and emit its outcome, plus a duration on anything but an abort. A key that
 * is not open is a no-op, which is how a background refresh or a second settle emits nothing.
 */
const settleAvailabilityInteraction = ({ key, outcome, reason, source }: SettleParams): void =>
    interactions.settle({ key: key, outcome: outcome, reason: reason, context: { source: source } });

/** End an open interaction the operator walked away from. Outcome only, never a duration. */
const abortAvailabilityInteraction = (key: string): void => interactions.abort(key);

const isAvailabilityInteractionOpen = (key: string): boolean => interactions.isOpen(key);

export {
    abortAvailabilityInteraction,
    isAvailabilityInteractionOpen,
    settleAvailabilityInteraction,
    startAvailabilityInteraction,
};
